import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { uploadKYCDocument, getKYCDocuments } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Upload, Eye } from '@phosphor-icons/react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KYC = () => {
  const { user, refresh } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [documentType, setDocumentType] = useState('PASSPORT');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    try {
      const response = await getKYCDocuments();
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchDocuments();
    // Re-pull the user record so an admin-triggered kyc_status change lands
    // without requiring a full logout/login. AuthContext exposes `refresh`.
    if (typeof refresh === 'function') refresh();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setUploading(true);
    try {
      await uploadKYCDocument(documentType, file);
      toast.success('Document uploaded successfully');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROVED': return <CheckCircle size={20} className="text-trading-bullish" weight="fill" />;
      case 'REJECTED': return <XCircle size={20} className="text-trading-bearish" weight="fill" />;
      default: return <Clock size={20} className="text-yellow-600" weight="fill" />;
    }
  };

  return (
    <div className="p-8" data-testid="client-kyc-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-primary">KYC Verification</h1>
        <p className="text-slate-600 mt-1">Upload required identification documents</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-2">
          {getStatusIcon(user?.kyc_status)}
          <span className="font-semibold text-lg">Current Status: {user?.kyc_status?.replace('_', ' ') || 'NOT STARTED'}</span>
        </div>
        <p className="text-sm text-slate-600">
          {user?.kyc_status === 'APPROVED' && '✓ Your account is fully verified.'}
          {user?.kyc_status === 'PENDING' && 'Your documents are under review.'}
          {user?.kyc_status === 'REJECTED' && 'Please resubmit your documents.'}
          {(!user?.kyc_status || user?.kyc_status === 'NOT_STARTED') && 'Please upload your ID and proof of address to complete verification.'}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Upload Document</h2>
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="text-sm font-semibold">Document Type</label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger data-testid="kyc-doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PASSPORT">Passport</SelectItem>
                <SelectItem value="ID_CARD">National ID Card</SelectItem>
                <SelectItem value="PROOF_OF_ADDRESS">Proof of Address</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-semibold">Select File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={e => setFile(e.target.files[0])}
              className="w-full mt-1 border border-slate-300 rounded-md p-2"
              data-testid="kyc-file-input"
            />
            <p className="text-xs text-slate-500 mt-1">Accepted: JPG, PNG, PDF. Max 10MB.</p>
          </div>
          <Button type="submit" disabled={uploading || !file} data-testid="kyc-upload-submit">
            <Upload size={18} className="mr-2" />
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-brand-primary">Your Documents</h2>
        </div>
        <div className="p-6">
          {loading ? <div className="loader mx-auto"></div>
            : documents.length === 0 ? <p className="text-center text-slate-500">No documents uploaded yet</p>
            : (
              <table className="w-full text-sm" data-testid="kyc-documents-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                    <th className="pb-3">Type</th>
                    <th className="pb-3">File Name</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Notes</th>
                    <th className="pb-3">Uploaded</th>
                    <th className="pb-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map(doc => (
                    <tr key={doc.id} className="border-b border-slate-100">
                      <td className="py-3">{doc.document_type}</td>
                      <td className="py-3">{doc.file_name}</td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(doc.status)}
                          <span className="text-xs font-semibold">{doc.status}</span>
                        </div>
                      </td>
                      <td className="py-3 text-xs text-slate-600">{doc.admin_notes || '-'}</td>
                      <td className="py-3 text-xs text-slate-500">{new Date(doc.uploaded_at).toLocaleString()}</td>
                      <td className="py-3">
                        {doc.storage_path ? (
                          <Button size="sm" variant="outline" data-testid={`kyc-view-${doc.id}`}
                            onClick={async () => {
                              try {
                                const r = await axios.get(`${API}/kyc/documents/${doc.id}/download`, { responseType: 'blob' });
                                const url = URL.createObjectURL(r.data);
                                window.open(url, '_blank', 'noopener,noreferrer');
                                setTimeout(() => URL.revokeObjectURL(url), 60_000);
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || 'Download failed');
                              }
                            }}>
                            <Eye size={14} className="mr-1" /> View
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Legacy</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
};

export default KYC;
